import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import type { RuleSet } from 'styled-components';

import { SafariCompatModal as Modal } from '../../../styles/SafariCompatModal';
import { DRAWER_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import { CLOSE_TIMEOUT_MS } from '../constants/timeout';
import * as styles from '../styles/Drawer.styles';

export const OVERLAY_DRAWER_TESTID = 'overlay-drawer-testid';

const setDrawerHeight = () =>
  document.documentElement.style.setProperty(DRAWER_HEIGHT_CSS_VAR, `${window.innerHeight}px`);

export interface DrawerProps {
  children: ReactNode | undefined;
  contentContainerStyles?: RuleSet<object>;
  contentLabel: string;
  describedBy?: string;
  drawerWidth?: string;
  isOpen: boolean;
  onAfterClose?: () => void;
  onOverlayClick?: () => void;
  onRequestClose: () => void;
  panelSize?: 'full' | 'partial';
  transitionFrom?: 'right' | 'left' | 'bottom';
}

const DRAWER_CONTENT_CLASS_NAMES: styles.DrawerGlobalStylesProps['contentClassNames'] = {
  base: 'drawer-content',
  afterOpen: 'drawer-content--afterOpen',
  beforeClose: 'drawer-content--beforeClose',
};

const DRAWER_OVERLAY_CLASS_NAMES: styles.DrawerGlobalStylesProps['overlayClassNames'] = {
  base: 'drawer-overlay',
  afterOpen: 'drawer-overlay--afterOpen',
  beforeClose: 'drawer-overlay--beforeClose',
};

const DrawerGlobalStyles = styles.DrawerGlobalStyles({
  contentClassNames: DRAWER_CONTENT_CLASS_NAMES,
  overlayClassNames: DRAWER_OVERLAY_CLASS_NAMES,
  closeTimeoutMS: CLOSE_TIMEOUT_MS,
});

export const Drawer = ({
  children,
  contentContainerStyles,
  contentLabel,
  describedBy,
  isOpen,
  onAfterClose,
  onOverlayClick,
  onRequestClose,
  panelSize = 'full',
  transitionFrom = 'right',
}: DrawerProps) => {
  useEffect(() => {
    const handler = () => {
      setDrawerHeight();
    };

    handler();
    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <>
      <DrawerGlobalStyles />
      <Modal
        aria={{ modal: true, describedby: describedBy || '' }}
        className={DRAWER_CONTENT_CLASS_NAMES}
        closeTimeoutMS={CLOSE_TIMEOUT_MS}
        contentLabel={contentLabel}
        data={{ 'transition-from': transitionFrom, 'panel-size': panelSize }}
        isOpen={isOpen}
        onAfterClose={onAfterClose}
        onAfterOpen={() => {
          setDrawerHeight();
        }}
        onRequestClose={onRequestClose}
        overlayClassName={DRAWER_OVERLAY_CLASS_NAMES}
      >
        {onOverlayClick && (
          <div
            aria-hidden
            css={styles.modalOverlay}
            data-testid={OVERLAY_DRAWER_TESTID}
            onClick={onOverlayClick}
          />
        )}
        <div css={[styles.drawerContent, contentContainerStyles]}>{children}</div>
      </Modal>
    </>
  );
};

export const setAppElement = ReactModal.setAppElement;
